/**
 * Card number helpers for the card forms (Joe, 2026-11-04): show the brand as the customer types, space the
 * digits the way the card does, size the security code, and catch a mistyped number before it is sent.
 * Nothing here leaves the browser; the number still goes to the payment processor only through Accept.js.
 *
 * The same file is in apps/customer/src/cardNumber.ts; keep the two identical.
 */
export type Brand = "Visa" | "Mastercard" | "Amex" | "Discover" | null;

/** Brand from the leading digits (a partial number is fine). */
export function cardBrand(input: string): Brand {
  const d = input.replace(/\D/g, "");
  if (/^3[47]/.test(d)) return "Amex";
  if (/^4/.test(d)) return "Visa";
  if (/^5[1-5]/.test(d)) return "Mastercard";
  if (/^2/.test(d)) {
    const p = Number(d.slice(0, 4).padEnd(4, "0"));
    if (d.length >= 2 && p >= 2221 && p <= 2720) return "Mastercard";
    if (d.length < 4 && /^2[2-7]/.test(d)) return "Mastercard";
  }
  if (/^6011|^64[4-9]|^65/.test(d)) return "Discover";
  if (/^622/.test(d) && d.length >= 6) {
    const p = Number(d.slice(0, 6));
    if (p >= 622126 && p <= 622925) return "Discover";
  }
  return null;
}

/** Digit groups: Amex 4-6-5, everything else 4-4-4-4 (and a last group of 3 for 19-digit cards). */
const groups = (b: Brand) => (b === "Amex" ? [4, 6, 5] : [4, 4, 4, 4, 3]);

/** Longest number for the brand (Amex 15, Visa and Discover up to 19, Mastercard 16, unknown 19). */
export const maxDigits = (b: Brand) => (b === "Amex" ? 15 : b === "Mastercard" ? 16 : 19);

/** Lengths that count as a complete number. */
export const validLengths = (b: Brand): number[] =>
  b === "Amex" ? [15] : b === "Mastercard" ? [16] : b === "Visa" ? [13, 16, 19] : b === "Discover" ? [16, 17, 18, 19] : [12, 13, 14, 15, 16, 17, 18, 19];

/** Security code length: 4 for Amex, 3 otherwise. */
export const codeLength = (b: Brand) => (b === "Amex" ? 4 : 3);

/** The number as typed, cut to the brand's length and spaced: "4111 1111 1111 1111", "3782 822463 10005". */
export function formatCardNumber(input: string): string {
  const b = cardBrand(input);
  const d = input.replace(/\D/g, "").slice(0, maxDigits(b));
  const out: string[] = [];
  let i = 0;
  for (const g of groups(b)) {
    if (i >= d.length) break;
    out.push(d.slice(i, i + g));
    i += g;
  }
  return out.join(" ");
}

/** The Luhn check digit test every card number passes. */
export function luhnOk(input: string): boolean {
  const d = input.replace(/\D/g, "");
  if (!d) return false;
  let sum = 0;
  for (let i = 0; i < d.length; i++) {
    let n = Number(d[d.length - 1 - i]);
    if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
  }
  return sum % 10 === 0;
}

/** The usual length, where a number is checked while still being typed: Amex 15, others 16. */
const usualDigits = (b: Brand) => (b === "Amex" ? 15 : 16);

/**
 * A problem with the number to show the customer, or null. While typing (`final` false) it only speaks once the
 * number reaches its usual length; on submit (`final` true) a short number counts too.
 */
export function cardNumberProblem(input: string, final: boolean): string | null {
  const d = input.replace(/\D/g, "");
  const b = cardBrand(d);
  if (!d) return final ? "Enter the card number." : null;
  if (!final && d.length < usualDigits(b)) return null;
  if (!validLengths(b).includes(d.length)) return final ? "The card number is too short." : null;
  return luhnOk(d) ? null : "That card number doesn't look right. Please check it.";
}

/** A problem with the security code, or null. Empty is allowed only when `optional`. */
export function cardCodeProblem(code: string, cardNumber: string, optional = false): string | null {
  const c = code.trim();
  if (!c) return optional ? null : "Enter the security code.";
  const n = codeLength(cardBrand(cardNumber));
  return /^\d+$/.test(c) && c.length === n ? null : `The security code is ${n} digits${n === 4 ? " (on the front of the card)" : " (on the back of the card)"}.`;
}
